import React, { useState, useEffect, useCallback } from 'react';
import { adminSeoAPI } from '../services/api';
import { useAlert } from '../context/AlertContext';
import '../styles/SeoSettings.css';

const PAGES = [
  // Static pages
  { key: 'home',        label: 'Home',               url: 'ecbiolabs.com/' },
  { key: 'about',       label: 'About Us',            url: 'ecbiolabs.com/about-us' },
  { key: 'contact',     label: 'Contact',             url: 'ecbiolabs.com/contact' },
  { key: 'services',    label: 'Services',            url: 'ecbiolabs.com/services' },
  { key: 'products',    label: 'Products (Listing)',  url: 'ecbiolabs.com/Products' },
  { key: 'blogs',       label: 'Blogs',               url: 'ecbiolabs.com/blogs' },
  { key: 'promotions',  label: 'Promotions',          url: 'ecbiolabs.com/promotions' },
  // Product category pages
  { key: 'product-category-antibodies',                  label: 'Category: Antibodies',                   url: 'ecbiolabs.com/product-category/antibodies' },
  { key: 'product-category-elisa-kits-multiplex-panels', label: 'Category: ELISA Kits & Multiplex Panels', url: 'ecbiolabs.com/product-category/elisa-kits-multiplex-panels' },
  { key: 'product-category-cell-lines-and-culture-products', label: 'Category: Cell Lines & Culture Products', url: 'ecbiolabs.com/product-category/cell-lines-and-culture-products' },
  { key: 'product-category-biochemicals',                label: 'Category: Biochemicals',                 url: 'ecbiolabs.com/product-category/biochemicals' },
  { key: 'product-category-proteins-and-peptides',       label: 'Category: Proteins & Peptides',          url: 'ecbiolabs.com/product-category/proteins-and-peptides' },
  { key: 'product-category-nucleic-acid-extraction-kits', label: 'Category: Nucleic Acid Extraction Kits', url: 'ecbiolabs.com/product-category/nucleic-acid-extraction-kits' },
  { key: 'product-category-nanoparticles-beads',         label: 'Category: Nanoparticles & Beads',        url: 'ecbiolabs.com/product-category/nanoparticles-beads' },
  { key: 'product-category-assay-kits',                  label: 'Category: Assay Kits',                   url: 'ecbiolabs.com/product-category/assay-kits' },
  { key: 'product-category-molecular-biology-products',  label: 'Category: Molecular Biology Products',   url: 'ecbiolabs.com/product-category/molecular-biology-products' },
  { key: 'product-category-plastic-wares',               label: 'Category: Plastic Wares',                url: 'ecbiolabs.com/product-category/plastic-wares' },
];

const EMPTY_FORM = {
  metaTitle: '',
  metaDescription: '',
  ogTitle: '',
  ogDescription: '',
  ogImage: '',
  robots: 'index, follow',
  canonicalOverride: '',
  schemaJson: '',
};

const SeoSettings = () => {
  const { showAlert } = useAlert();
  const [selectedKey, setSelectedKey] = useState(PAGES[0].key);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schemaError, setSchemaError] = useState('');
  const [showOg, setShowOg] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const selectedPage = PAGES.find(p => p.key === selectedKey);

  const fetchSeo = useCallback(async (pageKey) => {
    setLoading(true);
    try {
      const res = await adminSeoAPI.getByPageKey(pageKey);
      const data = res.data.data || {};
      setFormData({
        metaTitle:        data.metaTitle        || '',
        metaDescription:  data.metaDescription  || '',
        ogTitle:          data.ogTitle          || '',
        ogDescription:    data.ogDescription    || '',
        ogImage:          data.ogImage          || '',
        robots:           data.robots           || 'index, follow',
        canonicalOverride: data.canonicalOverride || '',
        schemaJson:       data.schemaJson       || '',
      });
    } catch {
      setFormData(EMPTY_FORM);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSeo(selectedKey);
    setSchemaError('');
  }, [selectedKey, fetchSeo]);

  const handlePageSelect = (key) => {
    setSelectedKey(key);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'schemaJson') setSchemaError('');
  };

  const validateSchema = () => {
    if (!formData.schemaJson.trim()) return true;
    try {
      JSON.parse(formData.schemaJson);
      return true;
    } catch {
      setSchemaError('Invalid JSON — please check your schema syntax.');
      return false;
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateSchema()) return;
    setSaving(true);
    try {
      await adminSeoAPI.upsert(selectedKey, {
        ...formData,
        pageLabel: selectedPage?.label,
      });
      showAlert('SEO settings saved successfully!', 'success');
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to save SEO settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Live Google snippet preview
  const previewTitle = formData.metaTitle || `${selectedPage?.label} | EC Biolabs`;
  const previewUrl   = `https://${selectedPage?.url}`;
  const previewDesc  = formData.metaDescription || 'No meta description set.';

  return (
    <div className="seo-settings-page">
      <div className="seo-settings-header">
        <h1>SEO Settings</h1>
        <p>Manage meta titles, descriptions, and Open Graph tags for each page.</p>
      </div>

      <div className="seo-settings-layout">
        {/* Left panel — page list */}
        <aside className="seo-page-list">
          <div className="seo-page-list-group">
            <p className="seo-page-list-heading">Static Pages</p>
            {PAGES.slice(0, 7).map(page => (
              <button
                key={page.key}
                className={`seo-page-item ${selectedKey === page.key ? 'active' : ''}`}
                onClick={() => handlePageSelect(page.key)}
              >
                {page.label}
              </button>
            ))}
          </div>
          <div className="seo-page-list-group">
            <p className="seo-page-list-heading">Product Categories</p>
            {PAGES.slice(7).map(page => (
              <button
                key={page.key}
                className={`seo-page-item ${selectedKey === page.key ? 'active' : ''}`}
                onClick={() => handlePageSelect(page.key)}
              >
                {page.label.replace('Category: ', '')}
              </button>
            ))}
          </div>
        </aside>

        {/* Right panel — form */}
        <div className="seo-form-panel">
          {loading ? (
            <div className="seo-loading">Loading SEO data...</div>
          ) : (
            <form onSubmit={handleSave}>
              <div className="seo-form-section">
                <h2>{selectedPage?.label}</h2>
                <p className="seo-page-url">{previewUrl}</p>

                {/* Google Snippet Preview */}
                <div className="seo-snippet-preview">
                  <p className="snippet-label">Google search preview</p>
                  <div className="snippet-box">
                    <p className="snippet-title">{previewTitle}</p>
                    <p className="snippet-url">{previewUrl}</p>
                    <p className="snippet-desc">{previewDesc}</p>
                  </div>
                </div>

                {/* Meta Title */}
                <div className="seo-field">
                  <div className="seo-field-header">
                    <label htmlFor="metaTitle">Meta Title</label>
                    <span className={`char-count ${formData.metaTitle.length > 60 ? 'warn' : ''}`}>
                      {formData.metaTitle.length}/70
                    </span>
                  </div>
                  <input
                    type="text"
                    id="metaTitle"
                    name="metaTitle"
                    value={formData.metaTitle}
                    onChange={handleChange}
                    maxLength={70}
                    placeholder="Page title for search engines"
                  />
                </div>

                {/* Meta Description */}
                <div className="seo-field">
                  <div className="seo-field-header">
                    <label htmlFor="metaDescription">Meta Description</label>
                    <span className={`char-count ${formData.metaDescription.length > 180 ? 'warn' : ''}`}>
                      {formData.metaDescription.length}/200
                    </span>
                  </div>
                  <textarea
                    id="metaDescription"
                    name="metaDescription"
                    value={formData.metaDescription}
                    onChange={handleChange}
                    maxLength={200}
                    rows={3}
                    placeholder="Short description shown in search results"
                  />
                </div>
              </div>

              {/* ── Open Graph Toggle Section ── */}
              <div className="seo-form-section">
                <button
                  type="button"
                  className="seo-toggle-header"
                  onClick={() => setShowOg(prev => !prev)}
                >
                  <span>Open Graph (Social Sharing)</span>
                  <span className={`seo-toggle-icon ${showOg ? 'open' : ''}`}>&#8250;</span>
                </button>

                {showOg && (
                  <div className="seo-toggle-body">
                    {/* OG Title */}
                    <div className="seo-field">
                      <label htmlFor="ogTitle">OG Title</label>
                      <input
                        type="text"
                        id="ogTitle"
                        name="ogTitle"
                        value={formData.ogTitle}
                        onChange={handleChange}
                        maxLength={100}
                        placeholder="Defaults to Meta Title if left blank"
                      />
                    </div>

                    {/* OG Description */}
                    <div className="seo-field">
                      <label htmlFor="ogDescription">OG Description</label>
                      <input
                        type="text"
                        id="ogDescription"
                        name="ogDescription"
                        value={formData.ogDescription}
                        onChange={handleChange}
                        maxLength={200}
                        placeholder="Defaults to Meta Description if left blank"
                      />
                    </div>

                    {/* OG Image */}
                    <div className="seo-field">
                      <label htmlFor="ogImage">OG Image URL</label>
                      <input
                        type="url"
                        id="ogImage"
                        name="ogImage"
                        value={formData.ogImage}
                        onChange={handleChange}
                        placeholder="https://ecbiolabs.com/og-image.jpg (recommended: 1200×630px)"
                      />
                      {formData.ogImage && (
                        <div className="og-image-preview">
                          <img src={formData.ogImage} alt="OG preview" onError={e => e.target.style.display = 'none'} />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>



              <div className="seo-form-actions">
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save SEO Settings'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SeoSettings;
